import React, { useState, useEffect, useRef, useMemo } from "react";
import api from "@/api";
import { CO_LABEL } from "../utils/contractLabel";

export default function ContractModal({
  open,
  onClose,
  customerId,
  customerName,
  onCreated,
  contract,
  onUpdated,
  isAdmin = false,
}) {
  // A CO with projects can only be changed by an admin (the API checks it too)
  const numberLocked = Boolean(contract?.in_use) && !isAdmin;
  // While typing: the other contracts with this number (null = not checked yet, [] = available)
  const [coUsage, setCoUsage] = useState(null);
  const [co, setCo] = useState("");
  const [description, setDescription] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  // Always use customerId for the selected customer
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    customerId || ""
  );
  const modalRef = useRef(null);

  // Load customers: for the dropdown, or - when the customer is fixed - to show its name
  useEffect(() => {
    if (open) {
      api
        .post("/masterdata/customers")
        .then((res) => setCustomers(Array.isArray(res.data) ? res.data : []))
        .catch(() => setCustomers([]));
    }
  }, [open]);

  // Never show the internal customer id: the name passed in, else looked up
  const lockedCustomerName = customerName
    || customers.find((c) => String(c.id) === String(customerId))?.name
    || "Loading...";

  const sortedCustomers = useMemo(() => {
    if (!customers || !Array.isArray(customers)) return [];
    return customers.filter((c) => !c.closed).sort((a, b) => {
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  }, [customers]);

  // Keep form fields in sync with incoming props. Clear fields when opening for create.
  useEffect(() => {
    if (!open) return;
    if (contract) {
      // editing: populate fields from contract
      setCo(contract.co || "");
      setDescription(contract.description || "");
      setYear(contract.year || new Date().getFullYear());
      setSelectedCustomerId(contract.customer_id || "");
    } else {
      // creating: reset fields
      setCo("");
      setDescription("");
      setYear(new Date().getFullYear());
      if (customerId) setSelectedCustomerId(customerId);
      else setSelectedCustomerId("");
    }
    // also clear any previous error when modal opens
    setError(null);
  }, [customerId, open, contract]);

  // Tell whether the typed number is used already (debounced; the contract being edited doesn't count)
  useEffect(() => {
    if (!open || !co || (contract && String(contract.co) === String(co))) {
      setCoUsage(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .post("/masterdata/contracts", { co })
        .then((res) => {
          if (!cancelled) setCoUsage((Array.isArray(res?.data) ? res.data : []).filter((c) => c.id !== contract?.id));
        })
        .catch(() => { if (!cancelled) setCoUsage(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [co, open, contract]);

  // Dismiss on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    // Always send the customer ID
    const payload = {
      co,
      description,
      year,
      customer_id: customerId || selectedCustomerId,
    };
    // Use the same store endpoint for create and update; include id when editing
    const body = contract ? { id: contract.id, ...payload } : payload;
    api
      .post("/masterdata/contracts/store", body)
      .then((res) => {
        setCo("");
        setDescription("");
        setYear(new Date().getFullYear());
        if (contract) {
          // notify parent that update occurred
          try {
            window.dispatchEvent(
              new CustomEvent("app:toast", {
                detail: { type: "success", message: `${CO_LABEL} updated` },
              })
            );
          } catch (e) {}
          onUpdated && onUpdated();
        } else {
          try {
            window.dispatchEvent(
              new CustomEvent("app:toast", {
                detail: { type: "success", message: `${CO_LABEL} created` },
              })
            );
          } catch (e) {}
          onCreated && onCreated(res?.data?.contract ?? null);
        }
        onClose();
      })
      .catch((err) => {
        // Try to extract a meaningful error message from several common API shapes:
        // - { message: '...' }
        // - { errors: { field: ['msg1', 'msg2'] } }
        // - { errors: ['msg1', 'msg2'] }
        // - string
        let msg = "Failed to save contract";
        try {
          const d = err && err.response && err.response.data;
          if (!d) {
            msg = (err && err.message) || msg;
          } else if (typeof d === "string") {
            msg = d;
          } else if (d.message) {
            msg = d.message;
          } else if (d.error) {
            msg = d.error;
          } else if (Array.isArray(d.errors)) {
            msg = d.errors.join("; ");
          } else if (typeof d.errors === "object") {
            // Flatten object of arrays
            const parts = [];
            for (const k of Object.keys(d.errors)) {
              const v = d.errors[k];
              if (Array.isArray(v)) parts.push(...v);
              else if (typeof v === "string") parts.push(v);
            }
            if (parts.length) msg = parts.join("; ");
            else msg = JSON.stringify(d);
          } else {
            msg = JSON.stringify(d);
          }
        } catch (e) {
          msg = "Failed to save contract";
        }
        setError(msg);
        try {
          window.dispatchEvent(
            new CustomEvent("app:toast", {
              detail: { type: "error", message: msg },
            })
          );
        } catch (e) {}
      })
      .finally(() => setLoading(false));
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-900 rounded shadow-lg p-6 w-full max-w-md relative border border-gray-200 dark:border-gray-700"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
          {contract ? `Edit ${CO_LABEL}` : `New ${CO_LABEL}`}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="contract-customer" className="block text-sm text-gray-700 dark:text-gray-200">
              Customer
            </label>
            {customerId ? (
              <input
                id="contract-customer"
                className="border rounded px-2 py-1 w-full bg-gray-100 dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                value={lockedCustomerName}
                disabled
              />
            ) : (
              <select
                id="contract-customer"
                className="border rounded px-2 py-1 w-full bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select customer...
                </option>
                {sortedCustomers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label htmlFor="contract-co" className="block text-sm text-gray-700 dark:text-gray-200">
              {CO_LABEL}
            </label>
            <p id="contract-co-help" className="text-xs text-gray-500 dark:text-gray-400">
              The contract number ({CO_LABEL}) from the customer agreement.
            </p>
            <input
              className="border rounded px-2 py-1 w-full bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 read-only:bg-gray-100 read-only:text-gray-600 dark:read-only:bg-gray-900"
              id="contract-co"
              inputMode="numeric"
              maxLength={18}
              placeholder="e.g. 4107"
              aria-describedby="contract-co-help"
              value={co}
              onChange={(e) => setCo(e.target.value.replace(/\D/g, ""))}
              readOnly={numberLocked}
              required
            />
            {coUsage && (
              <p
                data-testid="co-usage"
                className={`mt-1 text-xs ${coUsage.length ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`}
              >
                {coUsage.length
                  ? `Already used by ${coUsage.map((c) => `${c.customer?.name ?? "?"} – ${c.description}`).join("; ")}`
                  : "Available"}
              </p>
            )}
            {numberLocked && (
              <p className="mt-1 text-xs text-gray-500">This {CO_LABEL} has projects, so only an admin can change it.</p>
            )}
            {contract?.in_use && isAdmin && String(contract.co) !== String(co) && (
              <p role="status" className="mt-1 text-xs text-amber-700 dark:text-amber-300">This {CO_LABEL} is in use: the new number applies everywhere, including its trials.</p>
            )}
          </div>
          <div>
            <label htmlFor="contract-description" className="block text-sm text-gray-700 dark:text-gray-200">
              Description
            </label>
            <input
              id="contract-description"
              className="border rounded px-2 py-1 w-full bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="contract-year" className="block text-sm text-gray-700 dark:text-gray-200">
              Year
            </label>
            <input
              id="contract-year"
              type="number"
              className="border rounded px-2 py-1 w-full bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              required
            />
          </div>
          {error && <div role="alert" className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-60"
              disabled={loading}
            >
              {loading ? "Saving..." : contract ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
